import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';

import { NvIcon } from '../../../shared/ui/icon/nv-icon';
import { NvDialogService } from '../../../shared/ui/dialog/nv-dialog.service';
import { TableDetailDialog, TableDetailDialogData } from '../../../shared/components/table-detail-dialog/table-detail-dialog';
import { FloorStore } from '../../../core/stores/floor.store';
import { OrdersStore } from '../../../core/stores/orders.store';
import { MenuStore } from '../../../core/stores/menu.store';
import { TableStatus } from '../../../shared/models/table-session.model';
import { Order } from '../../../shared/models/order.model';
import { MenuItem } from '../../../shared/models/menu.model';
import { formatMinorAsRupeesClient } from '../../../shared/utils/money.util';

const MAX_RESULTS_PER_GROUP = 5;

interface OrderMatch {
  order: Order;
  tableNumber: number | undefined;
}

/**
 * Concern 14 — the header's search box used to be a `<div>` + `<span>`,
 * `aria-hidden`, permanently `cursor: not-allowed`. This is the real thing:
 * filters client-side over data three already-shared stores hold, issuing
 * no query of its own (`nv-header.ts`'s own doc comment's original
 * constraint, kept even though the "no stores exist yet" reason it was
 * written for no longer applies).
 *
 * Deliberately its own component, mounted only while the search panel is
 * open (`@if` in nv-header.html) rather than inline in NvHeader — NvHeader
 * itself is always mounted, and `FloorStore`/`MenuStore` are NOT already
 * running app-wide the way `OrdersStore` is (via the always-mounted
 * requests-tasks-dock). Injecting them here means their listeners only
 * start for a staff member who actually opens search, not for every
 * console session.
 */
@Component({
  selector: 'nv-header-search-results',
  imports: [NvIcon],
  templateUrl: './header-search-results.html',
  styleUrl: './header-search-results.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderSearchResults {
  private readonly router = inject(Router);
  private readonly dialog = inject(NvDialogService);
  private readonly floor = inject(FloorStore);
  private readonly ordersStore = inject(OrdersStore);
  private readonly menu = inject(MenuStore);

  readonly query = input('');
  readonly closed = output<void>();

  readonly formatPrice = formatMinorAsRupeesClient;

  private readonly normalisedQuery = computed(() => this.query().trim().toLowerCase());
  readonly hasQuery = computed(() => this.normalisedQuery().length > 0);

  private readonly tableNumbers = computed(() => {
    const map = new Map<string, number>();
    for (const table of this.floor.tables()) map.set(table.id, table.number);
    return map;
  });

  readonly tableMatches = computed<TableStatus[]>(() => {
    const q = this.normalisedQuery();
    if (!q) return [];
    return this.floor
      .statuses()
      .filter((s) => String(s.number) === q || (s.guestName ?? '').toLowerCase().includes(q))
      .slice(0, MAX_RESULTS_PER_GROUP);
  });

  readonly orderMatches = computed<OrderMatch[]>(() => {
    const q = this.normalisedQuery();
    if (!q) return [];
    const numbers = this.tableNumbers();
    return this.ordersStore
      .orders()
      .filter((o) => o.status !== 'cancelled')
      .filter((o) => {
        const number = numbers.get(o.tableId);
        return (
          o.id.toLowerCase().startsWith(q) ||
          (number !== undefined && String(number) === q) ||
          o.lines.some((l) => l.name.toLowerCase().includes(q))
        );
      })
      .slice(0, MAX_RESULTS_PER_GROUP)
      .map((order) => ({ order, tableNumber: numbers.get(order.tableId) }));
  });

  readonly menuMatches = computed<MenuItem[]>(() => {
    const q = this.normalisedQuery();
    if (!q) return [];
    return this.menu
      .items()
      .filter((item) => item.name.toLowerCase().includes(q))
      .slice(0, MAX_RESULTS_PER_GROUP);
  });

  readonly hasResults = computed(
    () => this.tableMatches().length + this.orderMatches().length + this.menuMatches().length > 0
  );

  /** Opens the same live table-detail panel a floor-view click does — search can jump straight to the one thing a table match is usually for, rather than only landing on the floor and making staff find it again. */
  openTable(status: TableStatus): void {
    this.dialog.open<void, TableDetailDialogData>(TableDetailDialog, {
      data: { tableId: status.tableId },
      width: '480px',
    });
    this.closed.emit();
  }

  goToOrders(): void {
    void this.router.navigate(['/orders'], { queryParams: { view: 'active' } });
    this.closed.emit();
  }

  goToMenu(): void {
    void this.router.navigate(['/menu']);
    this.closed.emit();
  }
}
